import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Edit, 
  Users, 
  Eye, 
  Calendar,
  MapPin,
  Building,
  Briefcase,
  Star,
  Clock,
  Globe,
  Mail,
  DollarSign,
  FileText
} from 'lucide-react';
import { 
  Chip,
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Typography,
  Grid
} from '@mui/material';
import Button from '../../components/common/Button';
import adminApi from '../../api/admin';
import MarkdownViewer from '../../components/common/MarkdownViewer';
import { formatDateForDisplay } from '../../utils/helpers';

const AdminJobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState(0);
  const location = useLocation()
  const {job: jobPosting} = location.state || {}

  const { data: job, isLoading, isError, error } = useQuery({
    queryKey: ['job-details', id],
    queryFn: async () => {
      const res = await adminApi.getJobPostingDetails(id);
      return res.data;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="h-8 bg-gray-200 rounded w-2/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading job details: {error?.message}</p>
          <Button 
            onClick={() => navigate('/admin/jobs')} 
            variant="outline" 
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Job not found</p>
          <Button 
            onClick={() => navigate('/admin/jobs')} 
            variant="outline" 
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = {
    active: { color: 'success', label: 'Active' },
    draft: { color: 'warning', label: 'Draft' },
    closed: { color: 'error', label: 'Closed' },
    expired: { color: 'default', label: 'Expired' }
  };

  const status = statusConfig[job.status] || { color: 'default', label: job.status };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Mock stats since they're not in the API response
  const stats = [
    {
      label: 'Total Applicants',
      value: jobPosting.num_applicants || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      label: 'Total Views',
      value: jobPosting.num_views || 0,
      icon: Eye,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      label: 'Positions',
      value: job.no_of_positions || 1,
      icon: Briefcase,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      label: 'Days Active',
      value: Math.floor((new Date() - new Date(job.created_at)) / (1000 * 60 * 60 * 24)),
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    }
  ];

  const formatCurrency = (amount) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatSalaryRange = () => {
    if (!job.salary_min && !job.salary_max) return 'Not specified';
    if (job.salary_min && job.salary_max) {
      return `${formatCurrency(job.salary_min)} - ${formatCurrency(job.salary_max)}`;
    }
    return job.salary_min ? `From ${formatCurrency(job.salary_min)}` : `Up to ${formatCurrency(job.salary_max)}`;
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/jobs')}
            icon={<ArrowLeft className="w-4 h-4" />}
            className="p-2"
          />
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Job Details</h1>
            <p className="text-secondary-600">Manage and view job posting information</p>
          </div>
        </div>
        <div className="flex space-x-3">
          {/* <Button
            variant="outline"
            onClick={() => navigate(`/admin/jobs/${job.id}/edit`)}
            icon={<Edit className="w-4 h-4 mr-2" />}
          >
            Edit Job
          </Button> */}
          {/* <Button
            onClick={() => navigate(`/admin/jobs/${job.id}/applications`)}
            icon={<Users className="w-4 h-4 mr-2" />}
          >
            View Applications
          </Button> */}
        </div>
      </div>

      {/* Job Title and Status */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h2 className="text-2xl font-bold text-secondary-900">{job.title}</h2>
                <Chip 
                  label={status.label} 
                  color={status.color}
                  size="small"
                />
                {job.is_urgent && (
                  <Chip 
                    label="Urgent" 
                    color="error"
                    size="small"
                  />
                )}
              </div>
              <div className="flex flex-wrap items-center gap-4 text-secondary-600 mb-4">
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4" />
                  <span>{job.location}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Briefcase className="w-4 h-4" />
                  <span className="capitalize">{job.job_type?.replace('-', ' ')}</span>
                </div>
                {job.is_remote && (
                  <div className="flex items-center space-x-1">
                    <Globe className="w-4 h-4" />
                    <span>Remote</span>
                  </div>
                )}
                {job.contact_email && (
                  <div className="flex items-center space-x-1">
                    <Mail className="w-4 h-4" />
                    <span>{job.contact_email}</span>
                  </div>
                )}
              </div>
              {job.job_reference_code && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-secondary-600">Reference:</span>
                  <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                    {job.job_reference_code}
                  </code>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      <Grid container spacing={3} className="mb-6">
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg ${stat.bgColor} mr-4`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div>
                    <Typography variant="h4" className="font-bold text-secondary-900">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" className="text-secondary-600">
                      {stat.label}
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs Content */}
      <Card>
        <Tabs value={tabValue} onChange={handleTabChange} className="border-b">
          <Tab label="Job Details" />
          <Tab label="Description" />
          <Tab label="Requirements" />
        </Tabs>

        <CardContent className="p-6">
          {tabValue === 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-secondary-900">Basic Information</h3>
                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Job Type</span>
                    <span className="font-medium capitalize">{job.job_type?.replace('-', ' ')}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Experience Level</span>
                    <span className="font-medium capitalize">{job.experience_level}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Department</span>
                    <span className="font-medium capitalize">
                      {job.department?.replace('_', ' ') || 'Not specified'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Aircraft Type</span>
                    <span className="font-medium">{job.aircraft_type || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Positions Available</span>
                    <span className="font-medium">{job.no_of_positions || 1}</span>
                  </div>
                </div>
              </div>

              {/* Salary & Timeline */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-secondary-900">Compensation & Timeline</h3>
                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Salary Range</span>
                    <span className="font-medium">{formatSalaryRange()}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Created Date</span>
                    <span className="font-medium">
                      {formatDateForDisplay(job.created_at)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Last Updated</span>
                    <span className="font-medium">
                      {formatDateForDisplay(job.updated_at)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Expiry Date</span>
                    <span className="font-medium">
                      {job.expiry_date ? formatDateForDisplay(job.expiry_date) : 'Not set'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Expected Start</span>
                    <span className="font-medium">
                      {job.expected_start_date ? formatDateForDisplay(job.expected_start_date) : 'Not set'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Additional Settings */}
              <div className="lg:col-span-2">
                <h3 className="text-lg font-semibold mb-4 text-secondary-900">Additional Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="text-center p-4 border rounded-lg">
                    <span className="text-secondary-600 block text-sm">Visibility</span>
                    <Chip 
                      label={job.visibility} 
                      color={job.visibility === 'public' ? 'success' : 'default'}
                      size="small"
                      className="mt-2"
                    />
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <span className="text-secondary-600 block text-sm">Confidential</span>
                    <Chip 
                      label={job.confidential_posting ? 'Yes' : 'No'} 
                      color={job.confidential_posting ? 'warning' : 'default'}
                      size="small"
                      className="mt-2"
                    />
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <span className="text-secondary-600 block text-sm">Visa Requirements</span>
                    <span className="font-medium capitalize">{job.visa_requirements || 'Not specified'}</span>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <span className="text-secondary-600 block text-sm">Background Check</span>
                    <Chip 
                      label={job.background_check_required ? 'Required' : 'Not Required'} 
                      color={job.background_check_required ? 'warning' : 'default'}
                      size="small"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {tabValue === 1 && (
            <div className="space-y-6">
              <div>
                {/* <h3 className="text-lg font-semibold mb-3 text-secondary-900">Job Description</h3> */}
                <div className="prose max-w-none">
                  <MarkdownViewer className='text-secondary-700 leading-relaxed'>{job.description}</MarkdownViewer> 
                </div>
              </div>

              {job.responsibilities && (
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-secondary-900">Responsibilities</h3>
                  <div className="prose max-w-none">
                    <MarkdownViewer className="text-secondary-700 leading-relaxed">{job.responsibilities}</MarkdownViewer>
                  </div>
                </div>
              )}

              {job.benefits && (
                <div>
                  <h3 className="text-2xl font-semibold mb-3 text-secondary-900">Benefits</h3>
                  <div className="prose max-w-none">
                    <MarkdownViewer className="text-secondary-700 leading-relaxed">{job.benefits}</MarkdownViewer>
                  </div>
                </div>
              )}
            </div>
          )}

          {tabValue === 2 && (
            <div className="space-y-6">
              {/* License Requirements */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-secondary-900">License & Certification</h3>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">License Requirements</span>
                    <span className="font-medium">{job.license_requirements || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Medical Certification</span>
                    <span className="font-medium">{job.medical_certification_required || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-secondary-600">Medical Class</span>
                    <span className="font-medium">{job.medical_class || 'Not specified'}</span>
                  </div>
                </div>
              </div>

              {/* Flight Hours */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-secondary-900">Flight Experience</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="text-center p-4 border rounded-lg">
                    <span className="text-secondary-600 block text-sm">Minimum Flight Hours</span>
                    <span className="font-medium text-lg">{job.minimum_flight_hours || 'Not specified'}</span>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <span className="text-secondary-600 block text-sm">Total Flying Hours</span>
                    <span className="font-medium text-lg">{job.total_flying_hours_required || 'Not specified'}</span>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <span className="text-secondary-600 block text-sm">PIC Hours</span>
                    <span className="font-medium text-lg">{job.minimum_pic_hours || 'Not specified'}</span>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <span className="text-secondary-600 block text-sm">Recency (Months)</span>
                    <span className="font-medium text-lg">{job.recency_of_experience_months || 'Not specified'}</span>
                  </div>
                </div>
              </div>

              {/* Additional Requirements */}
              {(job.required_license_types?.length > 0 || job.endorsements_required?.length > 0) && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-secondary-900">Additional Requirements</h3>
                  <div className="space-y-2">
                    {job.required_license_types?.length > 0 && (
                      <div>
                        <span className="text-secondary-600 block mb-2">Required License Types:</span>
                        <div className="flex flex-wrap gap-2">
                          {job.required_license_types.map((license, index) => (
                            <Chip key={index} label={license} size="small" variant="outlined" />
                          ))}
                        </div>
                      </div>
                    )}
                    {job.endorsements_required?.length > 0 && (
                      <div>
                        <span className="text-secondary-600 block mb-2">Required Endorsements:</span>
                        <div className="flex flex-wrap gap-2">
                          {job.endorsements_required.map((endorsement, index) => (
                            <Chip key={index} label={endorsement} size="small" variant="outlined" />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminJobDetails;